// TUT-488 — o que o cartão híbrido do quadro de Propostas diz: o aviso "falta
// enviar", a idade, o próximo passo e o valor. Tudo sai do resumo da lista
// (estado, datas e o valor que o servidor calcula) — nenhum campo novo de
// negócio, e nenhuma condição comercial viaja para o quadro.
import { diasDeCalendarioAte } from "./format";
import type { Proposal, ProposalSummary } from "./types";

/**
 * Quantos dias sem resposta viram "aguardando retorno".
 *
 * Sete, e não um número redondo qualquer: é a mesma janela que o horizonte da
 * fila de peças já usa (`buildLegalBriefHorizon`, "próximos 7 dias"). O
 * escritório já lê "uma semana" como a unidade de espera em todo o produto, e
 * inventar outra régua aqui faria a mesma pergunta ter duas respostas. Mora
 * aqui, e `propostas-quadro.ts` a reexporta: a idade e a coluna leem a mesma
 * conta, e um import circular entre os dois não fecha.
 */
export const DIAS_PARA_AGUARDAR_RETORNO = 7;

/** Os campos do resumo que cada regra deste arquivo lê. */
type Resumo = ProposalSummary;
/** O que basta para contar os dias no estado (a coluna também precisa disso). */
type Datas = Pick<ProposalSummary, "status" | "createdAt" | "updatedAt" | "sentAt">;

/**
 * "Falta enviar" — o critério, definido pelo modelo atual de propostas.
 *
 * O envio só está REGISTRADO quando `sentAt` existe: ele é gravado na mesma
 * transação que põe a proposta em `SENT`, depois que o e-mail saiu. Logo,
 * falta enviar quando:
 *  1. a proposta está `ISSUED` — a versão foi emitida e ninguém a enviou; ou
 *  2. está `SENT` mas SEM `sentAt` — foi marcada como enviada sem o registro
 *     do envio (legado), e o status sozinho não prova que o cliente recebeu.
 *
 * Rascunho ainda não tem o que enviar; aceita, recusada e expirada já passaram
 * do envio (a emitida aceita direto, por telefone, não é "falta enviar": o
 * cliente já respondeu).
 */
export function faltaEnviar(proposta: Pick<Resumo, "status" | "sentAt">): boolean {
  return (
    proposta.status === "ISSUED" ||
    (proposta.status === "SENT" && proposta.sentAt === null)
  );
}

export type TomDaIdade = "neutro" | "atencao" | "critico";

export interface IdadeDaProposta {
  /** Dias de calendário, no fuso do escritório, desde que ela chegou ao estado. */
  dias: number;
  /** "enviada há 9 dias" — o verbo do estado. */
  rotulo: string;
  tom: TomDaIdade;
}

/** Duas semanas: a enviada sem resposta vira problema, não só espera. */
const DIAS_PARA_ALERTA_CRITICO = 14;

const VERBO_DO_ESTADO: Record<ProposalSummary["status"], string> = {
  DRAFT: "criada",
  ISSUED: "emitida",
  SENT: "enviada",
  ACCEPTED: "aceita",
  DECLINED: "recusada",
  EXPIRED: "expirada",
  CANCELLED: "cancelada",
};

/**
 * Desde quando a proposta está neste estado.
 *
 * - rascunho: a criação (o `updatedAt` dele muda a cada edição);
 * - enviada: o ENVIO REGISTRADO — e, sem registro, a última mudança de estado;
 * - os demais: `updatedAt`, que depois do rascunho só muda na transição
 *   (emitir, enviar, responder).
 */
function desdeQuando(proposta: Datas): string {
  if (proposta.status === "DRAFT") return proposta.createdAt;
  if (proposta.status === "SENT" && proposta.sentAt) return proposta.sentAt;
  return proposta.updatedAt;
}

/**
 * A ordem de "Aguardando retorno": a que espera há MAIS tempo primeiro, como no
 * protótipo — é a que mais precisa de cobrança. Sem isto a coluna saía na ordem
 * do servidor (a mais recente em cima), a que menos precisa. As outras colunas
 * seguem a ordem do servidor.
 */
export function maisAntigaPrimeiro(a: Datas, b: Datas): number {
  return new Date(desdeQuando(a)).getTime() - new Date(desdeQuando(b)).getTime();
}

/** Dias de calendário desde o estado — a MESMA conta da coluna "Aguardando retorno". */
export function diasNoEstado(proposta: Datas, agora: Date): number {
  return Math.max(0, -diasDeCalendarioAte(desdeQuando(proposta), agora));
}

function haQuantoTempo(dias: number): string {
  if (dias === 0) return "hoje";
  return dias === 1 ? "há 1 dia" : `há ${dias} dias`;
}

export function idadeDaProposta(proposta: Resumo, agora: Date): IdadeDaProposta {
  const dias = diasNoEstado(proposta, agora);
  // O alerta é de quem espera resposta e tem o envio registrado: a aceita há
  // três semanas não é problema, e a emitida que ninguém enviou já tem o aviso
  // "falta enviar".
  const esperaResposta = proposta.status === "SENT" && proposta.sentAt !== null;
  const tom: TomDaIdade = !esperaResposta
    ? "neutro"
    : dias >= DIAS_PARA_ALERTA_CRITICO
      ? "critico"
      : dias >= DIAS_PARA_AGUARDAR_RETORNO
        ? "atencao"
        : "neutro";
  return {
    dias,
    rotulo: `${VERBO_DO_ESTADO[proposta.status]} ${haQuantoTempo(dias)}`,
    tom,
  };
}

/** O que fazer a seguir, do estado e da idade; `null` quando não há o que fazer. */
export function proximoPasso(proposta: Resumo, agora: Date): string | null {
  switch (proposta.status) {
    case "DRAFT":
      return "Completar e emitir";
    case "ISSUED":
      return "Enviar ao cliente";
    case "SENT":
      if (proposta.sentAt === null) return "Enviar ao cliente";
      return diasNoEstado(proposta, agora) >= DIAS_PARA_AGUARDAR_RETORNO
        ? "Cobrar retorno"
        : "Aguardar resposta";
    case "ACCEPTED":
      // O aceite da Assistência abre o contrato; os outros dois tipos abrem o
      // trabalho especial (ver `ensureDemoContract` e o `recordResponse`).
      return proposta.serviceType === "ASSISTANCE"
        ? "Abrir o contrato"
        : "Abrir o trabalho especial";
    default:
      return null;
  }
}

export type ValorDoCartao =
  | { tipo: "total"; cents: number }
  | { tipo: "tabela"; referenciaCents: number }
  | { tipo: "sem-valor" };

/**
 * O valor do cartão: o total da proposta, ou — na Assistência com tabela de
 * cinco preços, que cobra por etapa de cada processo e não tem total — a
 * "Tabela de preços", com a soma só de referência.
 */
export function valorDoCartao(
  proposta: Pick<Resumo, "valueCents" | "tableReferenceCents">,
): ValorDoCartao {
  if (proposta.valueCents !== null) return { tipo: "total", cents: proposta.valueCents };
  if (proposta.tableReferenceCents !== null) {
    return { tipo: "tabela", referenciaCents: proposta.tableReferenceCents };
  }
  return { tipo: "sem-valor" };
}

/**
 * O valor do resumo a partir das condições — o espelho de
 * `valoresDaProposta` (`apps/api/src/domain/proposal-values.ts`), que o
 * servidor aplica. Só o modo demonstração o chama: com a API de verdade o
 * resumo já chega com os dois números.
 */
export function valoresDasCondicoes(
  proposta: Pick<Proposal, "assistanceTerms" | "consultingSpecialTerms">,
): Pick<ProposalSummary, "valueCents" | "tableReferenceCents"> {
  const consultoria = proposta.consultingSpecialTerms;
  if (consultoria) return { valueCents: consultoria.totalPriceCents, tableReferenceCents: null };
  const termos = proposta.assistanceTerms;
  if (!termos) return { valueCents: null, tableReferenceCents: null };
  if (termos.billingMode === "GLOBAL") return { valueCents: termos.globalPriceCents, tableReferenceCents: null };
  const precos = [
    termos.questionsPriceCents,
    termos.assistantNominationPriceCents,
    termos.fieldCollectionPriceCents,
    termos.reportPriceCents,
    termos.impugnationPriceCents,
  ];
  return {
    valueCents: null,
    tableReferenceCents: precos.every((preco) => preco === null)
      ? null
      : precos.reduce<number>((soma, preco) => soma + (preco ?? 0), 0),
  };
}

export interface TotaisDaColuna {
  quantidade: number;
  /** A soma dos TOTAIS: a tabela de preços não entra. */
  totalCents: number;
  /** Quantas ficaram fora da soma por serem tabela de preços ("+ 2 tabelas de preços"). */
  tabelas: number;
}

export function totaisDaColuna(
  propostas: Pick<Resumo, "valueCents" | "tableReferenceCents">[],
): TotaisDaColuna {
  const totais: TotaisDaColuna = { quantidade: propostas.length, totalCents: 0, tabelas: 0 };
  for (const proposta of propostas) {
    const valor = valorDoCartao(proposta);
    if (valor.tipo === "total") totais.totalCents += valor.cents;
    else if (valor.tipo === "tabela") totais.tabelas += 1;
  }
  return totais;
}

/** O tipo no cartão: a Assistência diz também a modalidade (contrato ou avulsa). */
export function rotuloDoTipo(
  proposta: Pick<ProposalSummary, "serviceType" | "assistanceModality">,
): string {
  if (proposta.serviceType === "ASSISTANCE") {
    return proposta.assistanceModality === "SINGLE_EXPERTISE"
      ? "Assistência · avulsa"
      : proposta.assistanceModality === "CONTRACT"
        ? "Assistência · contrato"
        : "Assistência";
  }
  return proposta.serviceType === "CONSULTING" ? "Consultoria" : "Especial";
}

/** "+ 2 tabelas de preços" — o que ficou fora da soma, dito por extenso. */
export function rotuloDasTabelas(tabelas: number): string | null {
  if (tabelas === 0) return null;
  return tabelas === 1 ? "+ 1 tabela de preços" : `+ ${tabelas} tabelas de preços`;
}

/** A decidida que passou desta idade sai do quadro até a pessoa pedir um período. */
export const DIAS_DAS_DECIDIDAS_NO_QUADRO = 30;

/**
 * A proposta foi decidida (aprovada ou reprovada) há mais tempo do que o
 * quadro mostra sem período pedido? Aprovadas e Reprovadas só crescem; sem
 * este recorte o quadro de quem fecha negócio há dois anos viraria uma lista.
 */
export function decididaHaMuito(proposta: Datas, agora: Date): boolean {
  return (
    (proposta.status === "ACCEPTED" ||
      proposta.status === "DECLINED" ||
      proposta.status === "EXPIRED") &&
    diasNoEstado(proposta, agora) > DIAS_DAS_DECIDIDAS_NO_QUADRO
  );
}
